import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { insightsApi } from '../api/insights';
import { Card } from '../components/Card';
import { DataTable, type Column } from '../components/DataTable';
import { Pill } from '../components/Pill';
import { Icon } from '../components/Icons';
import { fmtDate } from '../lib/format';
import type { UserSearchRow } from '../api/types';

export function UserLookupScreen() {
  const [q, setQ] = useState('');
  const [submitted, setSubmitted] = useState('');
  const navigate = useNavigate();

  const searching = submitted.trim().length > 0;

  const search = useQuery({
    queryKey: ['user-search', submitted],
    queryFn: () => insightsApi.searchUsers(submitted, 50),
    enabled: searching,
  });
  const recent = useQuery({
    queryKey: ['recent-users'],
    queryFn: () => insightsApi.recentUsers(50),
  });

  const rows = searching ? search.data ?? [] : recent.data ?? [];
  const loading = searching ? search.isFetching : recent.isFetching;
  const error = searching ? search.error : recent.error;

  const columns: Column<UserSearchRow>[] = [
    {
      key: 'id',
      header: 'User ID',
      sortKey: 'id',
      render: (r) => <span style={{ fontFamily: 'var(--font-mono)', color: 'var(--ink)' }}>{r.id}</span>,
    },
    {
      key: 'deviceUuid',
      header: 'Device UUID',
      render: (r) => (
        <span style={{ fontFamily: 'var(--font-mono)', fontSize: 12, color: 'var(--ink-2)' }}>
          {r.deviceUuid}
        </span>
      ),
    },
    {
      key: 'platform',
      header: 'Platform',
      hideMobile: true,
      render: (r) => <Pill size="sm">{r.platform}</Pill>,
    },
    {
      key: 'premium',
      header: 'Premium',
      sortKey: (r) => (r.premium ? 1 : 0),
      render: (r) =>
        r.premium ? <Pill tone="good" size="sm">Premium</Pill> : <Pill size="sm">Free</Pill>,
    },
    {
      key: 'createdAt',
      header: 'Joined',
      sortKey: (r) => new Date(r.createdAt).getTime(),
      render: (r) => <span style={{ color: 'var(--ink-2)' }}>{fmtDate(r.createdAt)}</span>,
    },
  ];

  const onSearch = () => setSubmitted(q.trim());

  return (
    <div style={{ padding: '24px 28px', display: 'flex', flexDirection: 'column', gap: 18 }}>
      <Card padding={18}>
        <div style={{ fontSize: 13, color: 'var(--ink-3)', marginBottom: 10 }}>
          Search a user by <strong style={{ color: 'var(--ink-2)' }}>user ID</strong> or{' '}
          <strong style={{ color: 'var(--ink-2)' }}>device UUID</strong>.
        </div>
        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 8,
              background: 'var(--bg-sunken)',
              border: '1px solid var(--line)',
              padding: '10px 14px',
              borderRadius: 'var(--r-md)',
              color: 'var(--ink-3)',
              flex: '1 1 320px',
            }}
          >
            <Icon.Search />
            <input
              value={q}
              autoFocus
              onChange={(e) => setQ(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') onSearch();
              }}
              placeholder="e.g. 1042  ·  firebase uid prefix  ·  device uuid"
              style={{
                border: 0,
                background: 'transparent',
                outline: 'none',
                fontFamily: 'var(--font-mono)',
                fontSize: 13,
                color: 'var(--ink)',
                flex: 1,
              }}
            />
          </div>
          <button
            onClick={onSearch}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 8,
              background: 'var(--brand)',
              color: '#fff',
              padding: '10px 20px',
              borderRadius: 'var(--r-md)',
              fontWeight: 600,
              fontSize: 13,
            }}
          >
            Search
            <Icon.Arrow />
          </button>
        </div>
      </Card>

      {error && (
        <Card padding={16}>
          <span style={{ color: 'var(--bad)', fontSize: 13 }}>
            {(error as Error).message}
          </span>
        </Card>
      )}

      <Card padding={0}>
        <div style={{ padding: '14px 18px', borderBottom: '1px solid var(--line)', display: 'flex', alignItems: 'baseline', justifyContent: 'space-between' }}>
          <div style={{ fontFamily: 'var(--font-display)', fontWeight: 600, fontSize: 15 }}>
            {searching ? 'Search results' : 'Recent users'}
          </div>
          <span style={{ fontSize: 12, color: 'var(--ink-3)' }}>
            {searching ? `for "${submitted}"` : 'latest signups · click a row'}
          </span>
        </div>
        <DataTable
          rows={rows}
          columns={columns}
          rowKey={(r) => r.id}
          loading={loading}
          onRowClick={(r) => navigate(`/users/${r.id}`)}
          emptyState={searching ? `No users match "${submitted}".` : 'No users yet.'}
        />
      </Card>
    </div>
  );
}
